// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";

import { ChargerCommand } from "graphql/api";
import {
  IAction,
  raiseAction,
  ActionType,
  IActionChargerCommand,
  IActionTypeConfirmPopup,
} from "actions";
import { IState } from "states/states";
import { IOcppChargingSession } from "states/ocppstates";
import { OcppCmdState } from "states/guistates";

import ModalStartTransaction from "view/modal/ModalStartTransaction";

const CMD_TIMEOUT = 10 * 1000; // command timeout
const CMD_OVER = 30 * 1000; // 20 s timeout, not showing any result anymore

interface IOcppCommandButtonsExtProps {
  chargerId: number;
  session: IOcppChargingSession | undefined;
}

interface IOcppCommandButtonsProps {
  canCtrlChargers: boolean;
  cmdState: OcppCmdState;
  cmdResult?: string;
  hasError: boolean;
  isOutdated: boolean;
}

const mapStateToProps = (state: IState) => {
  const user = state.site.userList?.find((curUser) => curUser.secId === state.site.user?.secId);
  const cmdTimer = Date.now() - (state.gui.chargerCmd.timestampMs ?? 0);

  const props: IOcppCommandButtonsProps = {
    canCtrlChargers: user?.accessRights.ocppChargerCtrl ?? false,
    cmdState: state.gui.chargerCmd.state,
    cmdResult: state.gui.chargerCmd.result,
    hasError:
      state.gui.chargerCmd.state !== OcppCmdState.CONFIRMED && cmdTimer > CMD_TIMEOUT
        ? true
        : state.gui.chargerCmd.hasError,
    isOutdated: cmdTimer > CMD_OVER,
  };
  return props;
};

interface IOcppCommandButtonsState {}

interface IOcppCommandButtonsDispatch {
  onStartTransaction: () => void;
  onChargerCmd: (title: string, mesage: string, cmd: ChargerCommand, cmdArgs?: any) => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IOcppCommandButtonsExtProps,
) => {
  const dispatchProps: IOcppCommandButtonsDispatch = {
    onStartTransaction: () => {
      dispatch(raiseAction(ActionType.ACT_START_TRANSACTION_POPUP));
    },
    onChargerCmd: (title: string, message: string, cmd: ChargerCommand, cmdArgs?: any) => {
      const confirmPayload: IActionChargerCommand = {
        chargerId: ownProps.chargerId,
        cmd,
        cmdArgs,
        timestampMs: Date.now(),
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_TRIGGER_CHARGER_CMD_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  confirmStopTitle: {
    id: "Stopping charging session",
  },
  confirmStopMessage: {
    id: "This will stop the ongoing charging session. Are you sure?",
  },
  confirmUnlockTitle: {
    id: "Unlocking connector",
  },
  confirmUnlockMessage: {
    id: "This will stop the ongoing charging session and unlock the charging connector. Are you sure?",
  },
  confirmResetTitle: {
    id: "Resetting charging session",
  },
  confirmResetMessage: {
    id: "This will trigger a reset of the charging session. Are you sure?",
  },
});

class OcppCommandButtonsComponent extends React.Component<
  IOcppCommandButtonsExtProps &
    IOcppCommandButtonsProps &
    IOcppCommandButtonsDispatch &
    WrappedComponentProps
> {
  state: IOcppCommandButtonsState = {};

  public render() {
    if (!this.props.canCtrlChargers) {
      return <></>;
    }
    const isOngoingSession = this.props.session && this.props.session.stopTimestamp === undefined;
    const isLocked =
      !this.props.hasError &&
      ![OcppCmdState.NONE, OcppCmdState.CONFIRMED].includes(this.props.cmdState);

    return (
      <div className="buttonbox" style={{ marginTop: 0, marginBottom: 10 }}>
        {this.props.canCtrlChargers &&
          (isOngoingSession ? (
            <>
              <Button
                variant="secondary"
                disabled={isLocked}
                onClick={() => {
                  this.props.onChargerCmd(
                    this.props.intl.formatMessage(messages.confirmStopTitle),
                    this.props.intl.formatMessage(messages.confirmStopMessage),
                    ChargerCommand.StopTransaction,
                    {
                      transactionId: this.props.session?.id,
                    },
                  );
                }}
              >
                <FormattedMessage id="Stop Charging" />
              </Button>
              <Button
                variant="secondary"
                disabled={isLocked}
                onClick={() => {
                  this.props.onChargerCmd(
                    this.props.intl.formatMessage(messages.confirmUnlockTitle),
                    this.props.intl.formatMessage(messages.confirmUnlockMessage),
                    ChargerCommand.Unlock,
                    {
                      connectorId: this.props.session?.connector,
                    },
                  );
                }}
              >
                <FormattedMessage id="Unlock Connector" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                disabled={isLocked}
                onClick={this.props.onStartTransaction}
              >
                <FormattedMessage id="Authorize Charging" />
              </Button>
              <ModalStartTransaction chargerId={this.props.chargerId} />
            </>
          ))}
        {this.props.canCtrlChargers && (
          <Button
            variant="secondary"
            disabled={isLocked}
            onClick={() => {
              this.props.onChargerCmd(
                this.props.intl.formatMessage(messages.confirmResetTitle),
                this.props.intl.formatMessage(messages.confirmResetMessage),
                ChargerCommand.Reset,
              );
            }}
          >
            <FormattedMessage id="Reboot Charger" />
          </Button>
        )}
        {this.props.cmdState !== OcppCmdState.NONE && !this.props.isOutdated && this.renderStatus()}
      </div>
    );
  }

  private renderStatus() {
    const alertClass = this.props.hasError
      ? "alert-danger"
      : this.props.cmdState === OcppCmdState.CONFIRMED
      ? "alert-success"
      : "alert-warning";

    return (
      <div className={`alert ${alertClass} button-sized-savefeedback`}>
        {this.props.cmdResult ? (
          this.props.cmdResult
        ) : (
          <>
            <FormattedMessage id={`OcppCmdStatus_${this.props.cmdState}`} />
            {this.props.hasError && (
              <>
                : <FormattedMessage id="Timeout Error" />
              </>
            )}
          </>
        )}
      </div>
    );
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(OcppCommandButtonsComponent),
);
